// src/components/alerts/AlertRulesTable.js
// --- UPDATE (Alert System AI): optional ruleHealth (FlowSense AI: noisy / flapping rules from
// the last 7 days) shows a badge under the rule name with the advice on hover, and a one-click
// "Apply" for the suggested duration (onApplyTuning). Without the props nothing changes.
import React from 'react';
import PropTypes from 'prop-types';
import {
    Table, Thead, Tbody, Tr, Th, Td, Button, ButtonGroup, Icon, Text,
    Badge, useColorModeValue, IconButton, Flex, Box, Tooltip
} from '@chakra-ui/react';
import { MdEdit, MdDelete, MdPlayCircleFilled, MdPauseCircleFilled, MdSensors, MdReceiptLong, MdConstruction } from 'react-icons/md';

function AlertRulesTable({ rules, onEdit, onDelete, onToggleEnable, isLoading, ruleHealth, onApplyTuning }) {
    const textColor = useColorModeValue("gray.700", "white");
    const tableRowHoverBg = useColorModeValue("gray.100", "gray.700");

    const getSeverityColorScheme = (severity) => {
        switch (severity?.toLowerCase()) {
            case 'critical': return 'red';
            case 'high': return 'orange';
            case 'medium': return 'yellow';
            case 'low': return 'green';
            default: return 'gray';
        }
    };

    // --- UPDATE: Helper to map numbers back to text for Water Levels ---
    const formatThreshold = (rule, value) => {
        if (rule.metric_type === 'level') {
            const val = parseFloat(value);
            if (val >= 100) return 'High (100)';
            if (val >= 60) return 'Medium (60)';
            if (val >= 30) return 'Low (30)';
            if (val >= 5) return 'Empty (5)';
        }
        return value;
    };
    // --- END UPDATE ---

    if (isLoading && (!rules || rules.length === 0)) {
        return <Text>Loading rules...</Text>;
    }

    if (!rules || rules.length === 0) {
        return <Text>No alert rules found. Create one to get started!</Text>;
    }

    return (
        <Box overflowX="auto">
            <Table variant="simple" color={textColor}>
                <Thead>
                    <Tr>
                        <Th>Name</Th>
                        <Th>Sensor / Asset</Th>
                        <Th>Metric</Th>
                        <Th>Condition</Th>
                        <Th>Severity</Th>
                        <Th>Duration (min)</Th>
                        <Th>Status</Th>
                        <Th>Auto WO</Th>
                        <Th>Actions</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {rules.map((rule) => (
                        <Tr key={rule.id} _hover={{ bg: tableRowHoverBg }}>
                            <Td>
                                <Text fontWeight="bold">{rule.name || 'N/A'}</Text>
                                <Text fontSize="sm" color="gray.500">ID: {rule.id}</Text>
                                {/* --- UPDATE (Alert System AI) */}
                                {ruleHealth && ruleHealth[rule.id] && (
                                    <Flex mt={1} gap={2} align="center" flexWrap="wrap">
                                        <Tooltip hasArrow label={ruleHealth[rule.id].advice}>
                                            <Badge colorScheme={ruleHealth[rule.id].flapping ? 'purple' : 'orange'} variant="subtle">
                                                AI: {ruleHealth[rule.id].label} · {ruleHealth[rule.id].alerts7d} alerts / 7 d
                                            </Badge>
                                        </Tooltip>
                                        {onApplyTuning && ruleHealth[rule.id].suggestedDuration && (
                                            <Button size="xs" variant="link" colorScheme="purple"
                                                onClick={() => onApplyTuning(rule.id, ruleHealth[rule.id].suggestedDuration)}>
                                                Set duration {ruleHealth[rule.id].suggestedDuration} min
                                            </Button>
                                        )}
                                    </Flex>
                                )}
                                {/* --- END UPDATE */}
                            </Td>
                            <Td>
                                <Flex align="center">
                                    <Icon as={MdSensors} mr={2} color="teal.500"/>
                                    <Box>
                                        <Text fontSize="sm" fontWeight="semibold">{rule.sensor_name || rule.sensor_id}</Text>
                                        <Text fontSize="xs" color="gray.400">{rule.sensor_location || 'No sensor location'}</Text>
                                         {rule.asset_id && (
                                            <Text fontSize="xs" mt={1}>
                                                Asset: {rule.asset_location_description || rule.asset_type_name || rule.asset_id}
                                            </Text>
                                        )}
                                    </Box>
                                </Flex>
                            </Td>
                            <Td>
                                <Badge colorScheme="purple" variant="outline">{rule.metric_type}</Badge>
                            </Td>
                            <Td>
                                {/* --- UPDATE: Render formatted threshold --- */}
                                {String(rule.condition_type || '').replace('_', ' ')} {formatThreshold(rule, rule.threshold_value)}
                                {rule.condition_type === 'outside_range' && rule.threshold_value_upper !== null 
                                    ? ` - ${formatThreshold(rule, rule.threshold_value_upper)}` 
                                    : ''}
                            </Td>
                            <Td>
                                <Badge colorScheme={getSeverityColorScheme(rule.severity)}>
                                    {rule.severity}
                                </Badge>
                            </Td>
                            <Td>{rule.duration_minutes}</Td>
                            <Td>
                                <Badge colorScheme={rule.is_enabled ? "green" : "red"}>
                                    {rule.is_enabled ? "Enabled" : "Disabled"}
                                </Badge>
                            </Td>
                            <Td>
                                <Icon as={MdConstruction} color={rule.auto_create_work_order ? "green.500" : "gray.400"} />
                            </Td>
                            <Td>
                                <ButtonGroup spacing={2} size="sm" variant="outline">
                                    <Tooltip label={rule.is_enabled ? "Disable Rule" : "Enable Rule"} placement="top">
                                        <IconButton
                                            icon={rule.is_enabled ? <MdPauseCircleFilled /> : <MdPlayCircleFilled />}
                                            colorScheme={rule.is_enabled ? "orange" : "green"}
                                            aria-label={rule.is_enabled ? "Disable Rule" : "Enable Rule"}
                                            onClick={() => onToggleEnable(rule)}
                                        />
                                    </Tooltip>
                                    <Tooltip label="Edit Rule" placement="top">
                                        <IconButton icon={<MdEdit />} aria-label="Edit Rule" onClick={() => onEdit(rule)} />
                                    </Tooltip>
                                    <Tooltip label="Delete Rule" placement="top">
                                        <IconButton icon={<MdDelete />} colorScheme="red" aria-label="Delete Rule" onClick={() => onDelete(rule.id)} />
                                    </Tooltip>
                                </ButtonGroup>
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table>
        </Box>
    );
}

AlertRulesTable.propTypes = {
    rules: PropTypes.array.isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onToggleEnable: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    ruleHealth: PropTypes.object, // --- UPDATE (Alert System AI)
    onApplyTuning: PropTypes.func, // --- UPDATE (Alert System AI)
};

export default AlertRulesTable;